import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { KeyRound, ShieldAlert } from 'lucide-react';
import mcpKeysService from '../../services/mcpKeysService';
import { describeApiError } from '../../utils/apiError';
import ApiKeyRevealDialog from '../shared/ApiKeyRevealDialog';
import InfoTip from '../shared/InfoTip';
import { PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';

const NAME_MAX = 200;

/**
 * Create a connection key in two steps: ask which app it is for, then show
 * the key once. The key, `connector_url` and `connect` exist only in the
 * create response, so the reveal can only be closed deliberately.
 *
 * Callbacks: onCreated(result) right after a successful create (the page keeps
 * `connect` in memory to fill in the instructions), onNoAccess() on 403,
 * onGoToKeys() from the "10 active keys" error.
 */
const CreateKeyDialog = ({ open, onClose, onCreated, onNoAccess, onGoToKeys }) => {
  const inputRef = useRef(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setName('');
      setError(null);
      setResult(null);
    }
  }, [open]);

  const pickChip = (chip) => {
    if (chip === 'Other') {
      setName('');
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setName(chip);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await mcpKeysService.createKey(name.trim());
      setResult(res);
      onCreated?.(res);
    } catch (err) {
      if (err?.status === 403) {
        onNoAccess?.();
        onClose?.();
        return;
      }
      setError({
        text: describeApiError(err, "Couldn't create the key."),
        // 400 = the user already has 10 active keys.
        showKeysLink: err?.status === 400,
      });
    } finally {
      setCreating(false);
    }
  };

  if (result) {
    return (
      <ApiKeyRevealDialog
        open={open}
        apiKey={result.key || ''}
        title={PLATFORM_MCP_COPY.keyCreatedTitle}
        subtitle={result.connect ? PLATFORM_MCP_COPY.keyCreatedSubtitle : null}
        requireExplicitDismiss
        onClose={onClose}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>
            {PLATFORM_MCP_COPY.keyShownOnce}
          </Typography>
          {result.connect?.warning && (
            <Alert severity="warning" icon={<ShieldAlert size={18} />}>
              {result.connect.warning}
            </Alert>
          )}
          {!result.connect && (
            <Alert severity="info" icon={<KeyRound size={18} />}>
              {PLATFORM_MCP_COPY.connectorNotSetUp}
            </Alert>
          )}
        </Box>
      </ApiKeyRevealDialog>
    );
  }

  return (
    <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700 }}>
        <KeyRound size={20} />
        {PLATFORM_MCP_COPY.createKey}
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          component="label"
          htmlFor="platform-mcp-key-name"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600, mb: 1 }}
        >
          {PLATFORM_MCP_COPY.keyNameLabel}
          <InfoTip title={PLATFORM_MCP_COPY.keyNameTip} size={14} />
        </Typography>
        <TextField
          id="platform-mcp-key-name"
          inputRef={inputRef}
          size="small"
          fullWidth
          autoFocus
          placeholder={PLATFORM_MCP_COPY.keyNamePlaceholder}
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, NAME_MAX))}
          onKeyDown={(e) => e.key === 'Enter' && !creating && handleCreate()}
          inputProps={{ maxLength: NAME_MAX }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {PLATFORM_MCP_COPY.keyNameChips.map((chip) => (
            <Chip
              key={chip}
              label={chip}
              size="small"
              variant={name === chip ? 'filled' : 'outlined'}
              onClick={() => pickChip(chip)}
            />
          ))}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error.text}
            {error.showKeysLink && (
              <>
                {' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    onClose?.();
                    onGoToKeys?.();
                  }}
                >
                  {PLATFORM_MCP_COPY.tooManyKeysLink}
                </Link>
              </>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={creating}
          startIcon={creating ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {creating ? 'Creating…' : 'Create key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateKeyDialog;
